import { createContext, useContext, useLayoutEffect, useMemo, useRef } from 'react';

export const REPORT_DATA_REQUEST_CONCURRENCY = 8;

type ReportDataRequestTask = () => Promise<void>;

interface PendingRequest {
  key: string;
  task: ReportDataRequestTask;
  resolve: () => void;
  reject: (reason?: unknown) => void;
}

export class ReportDataRequestQueue {
  private activeKeys = new Set<string>();
  private pendingRequests: PendingRequest[] = [];
  private disposed = false;

  constructor(private readonly concurrency: number) {}

  enqueue(key: string, task: ReportDataRequestTask): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const pendingIndex = this.pendingRequests.findIndex((request) => request.key === key);
      const nextRequest = { key, task, resolve, reject };

      if (pendingIndex >= 0) {
        this.pendingRequests[pendingIndex].resolve();
        this.pendingRequests[pendingIndex] = nextRequest;
      } else {
        this.pendingRequests.push(nextRequest);
      }

      this.drain();
    });
  }

  dispose() {
    this.disposed = true;
    this.pendingRequests.forEach((request) => request.resolve());
    this.pendingRequests = [];
  }

  activate() {
    this.disposed = false;
  }

  private drain() {
    if (this.disposed) {
      return;
    }

    while (this.activeKeys.size < this.concurrency) {
      const nextRequestIndex = this.pendingRequests.findIndex((request) => !this.activeKeys.has(request.key));

      if (nextRequestIndex < 0) {
        return;
      }

      const [nextRequest] = this.pendingRequests.splice(nextRequestIndex, 1);
      this.activeKeys.add(nextRequest.key);

      void Promise.resolve()
        .then(nextRequest.task)
        .then(nextRequest.resolve, nextRequest.reject)
        .finally(() => {
          this.activeKeys.delete(nextRequest.key);
          this.drain();
        });
    }
  }
}

const ReportDataRequestQueueContext = createContext<ReportDataRequestQueue | null>(null);

export function ReportDataRequestQueueProvider({ children }: React.PropsWithChildren) {
  const queueRef = useRef<ReportDataRequestQueue | null>(null);

  if (!queueRef.current) {
    queueRef.current = new ReportDataRequestQueue(REPORT_DATA_REQUEST_CONCURRENCY);
  }

  useLayoutEffect(() => {
    queueRef.current?.activate();

    return () => queueRef.current?.dispose();
  }, []);

  const queue = useMemo(() => queueRef.current as ReportDataRequestQueue, []);

  return <ReportDataRequestQueueContext.Provider value={queue}>{children}</ReportDataRequestQueueContext.Provider>;
}

export function useReportDataRequestQueue() {
  return useContext(ReportDataRequestQueueContext);
}
